/**
 * useTableSort
 *
 * Client-side multi-column sort state for the List & Grid Standard. Sort is currently
 * client-side (there is no base server `sort` param yet) — it orders the current page
 * for legacy grids or the full held set for adaptive grids (see `useAdaptiveGridData`).
 * See `.github/context/list-grid-standard-behaviours.context.md` §7.
 */
import { useCallback, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortState<TField extends string = string> {
  field: TField;
  dir: SortDirection;
}

export interface UseTableSortResult<TField extends string = string> {
  /** Active sort state, in priority order (first = primary sort). */
  sortState: SortState<TField>[];
  /** absent -> asc; asc -> desc; desc -> removed (off). Additional fields append. */
  toggle: (field: TField) => void;
  getSortDirection: (field: TField) => SortDirection | undefined;
  /** 1-based priority for `<SortPriority>` display when more than one sort is active. */
  getSortPriority: (field: TField) => number | undefined;
  reset: () => void;
}

/**
 * @param defaultField Default sort column (first sortable column unless the grid overrides).
 * @param defaultDir Default sort direction — 'asc' unless the grid overrides.
 * @param onChange Called whenever the sort state changes — wire to the page's resetPage()
 *   (toggling a sort resets to page 1 per the standard).
 */
export function useTableSort<TField extends string = string>(
  defaultField: TField,
  defaultDir: SortDirection = 'asc',
  onChange?: () => void
): UseTableSortResult<TField> {
  const [sortState, setSortState] = useState<SortState<TField>[]>([
    { field: defaultField, dir: defaultDir },
  ]);

  const toggle = useCallback(
    (field: TField) => {
      setSortState((prev) => {
        const index = prev.findIndex((s) => s.field === field);
        if (index === -1) {
          return [...prev, { field, dir: 'asc' as const }];
        }
        const current = prev[index];
        if (current.dir === 'asc') {
          const next = [...prev];
          next[index] = { field, dir: 'desc' };
          return next;
        }
        // desc -> remove (off)
        return prev.filter((_, i) => i !== index);
      });
      onChange?.();
    },
    [onChange]
  );

  const getSortDirection = useCallback(
    (field: TField) => sortState.find((s) => s.field === field)?.dir,
    [sortState]
  );

  const getSortPriority = useCallback(
    (field: TField): number | undefined => {
      if (sortState.length <= 1) return undefined;
      const index = sortState.findIndex((s) => s.field === field);
      return index === -1 ? undefined : index + 1;
    },
    [sortState]
  );

  const reset = useCallback(() => {
    setSortState([{ field: defaultField, dir: defaultDir }]);
  }, [defaultField, defaultDir]);

  return { sortState, toggle, getSortDirection, getSortPriority, reset };
}

/**
 * Shared comparator builder — used identically by the plain client sort and the
 * adaptive in-memory path (`useAdaptiveGridData`) so results never diverge.
 * Strings compare via `localeCompare`; everything else uses natural `< />`.
 */
export function makeSortComparator<T>(
  sortState: SortState<string>[],
  getValue: (item: T, field: string) => unknown = (item, field) =>
    (item as Record<string, unknown>)[field]
): (a: T, b: T) => number {
  return (a: T, b: T): number => {
    for (const { field, dir } of sortState) {
      const va = getValue(a, field);
      const vb = getValue(b, field);
      let cmp = 0;

      if (va == null && vb == null) {
        cmp = 0;
      } else if (va == null) {
        cmp = -1;
      } else if (vb == null) {
        cmp = 1;
      } else if (typeof va === 'string' && typeof vb === 'string') {
        cmp = va.localeCompare(vb);
      } else if (va < vb) {
        cmp = -1;
      } else if (va > vb) {
        cmp = 1;
      }

      if (cmp !== 0) {
        return dir === 'asc' ? cmp : -cmp;
      }
    }
    return 0;
  };
}
